import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { avatar } from '../../assets/assets';
import { useAuthStore } from '../../store/authStore';
import { useUpdateProfile, useChangePassword, useRequestSettingsChange, useChangeEmail, useUpdatePreferences, useUpdateUsername, useCheckUsernameAvailability, useGenerate2FA, useVerify2FASetup, useDisable2FA } from '../../services/user.service';
import { useAlert } from '../../context/AlertContext';
import { Eye, EyeOff, CheckCircle, Smartphone, X, Loader2 } from 'lucide-react';

const SettingsComponent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setTokens, clearTokens } = useAuthStore();
  const setAlert = useAlert();
  const { mutateAsync: updateProfile, isPending: isUpdatingProfile } = useUpdateProfile();
  const { mutateAsync: changePassword, isPending: isChangingPassword } = useChangePassword();
  const { mutateAsync: requestOTP, isPending: isRequestingOTP } = useRequestSettingsChange();
  const { mutateAsync: changeEmail, isPending: isChangingEmail } = useChangeEmail();
  const { mutateAsync: updatePreferences, isPending: isUpdatingPreferences } = useUpdatePreferences();
  const { mutateAsync: updateUsername, isPending: isUpdatingUsername } = useUpdateUsername();
  const { mutateAsync: checkUsername } = useCheckUsernameAvailability();
  const { mutateAsync: generate2FA, isPending: isGenerating2FA } = useGenerate2FA();
  const { mutateAsync: verify2FASetup, isPending: isVerifying2FA } = useVerify2FASetup();
  const { mutateAsync: disable2FA, isPending: isDisabling2FA } = useDisable2FA();

  const [activeTab, setActiveTab] = useState('edit-profile');
  const [profileImage, setProfileImage] = useState(user?.profilePicture || null);
  const [previewImage, setPreviewImage] = useState(user?.profilePicture || avatar);
  const [isUploading, setIsUploading] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null); 
  const [isOTPModalOpen, setIsOTPModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); 
  const [isSubmittingOTP, setIsSubmittingOTP] = useState(false);
  const [otpError, setOtpError] = useState('');
  
  // 2FA States
  const [is2FAModalOpen, setIs2FAModalOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [is2FASetup, setIs2FASetup] = useState(false); 
  const [twoFactorError, setTwoFactorError] = useState('');
  
  // Form states
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    username: user?.username || '',
    email: user?.email || '',
    address: user?.address || '',
    city: user?.city || '',
    postalCode: user?.postalCode || '',
    country: user?.country || '',
    timeZone: user?.timeZone || 'UTC+00:00',
    directMessages: user?.directMessages ?? false,
    twoFactor: false,
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    otpCode: ''
  });

  useEffect(() => {
    if (user) {
        setFormData(prev => ({
            ...prev,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            username: user.username || '',
            email: user.email || '',
            address: user.address || '',
            city: user.city || '',
            postalCode: user.postalCode || '',
            country: user.country || '',
            timeZone: user.timeZone || 'UTC+00:00'
        }));
        setProfileImage(user.profilePicture || null);
        setPreviewImage(user.profilePicture || avatar);
    }
  }, [user]);

  // Handle tab changes from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab && ['edit-profile', 'preferences', 'security', 'account'].includes(tab)) {
      setActiveTab(tab);
    } else if (!location.search) {
      updateUrlWithTab('edit-profile');
    }
  }, [location]);

  const updateUrlWithTab = (tab) => {

    navigate(user.role === 'user' ? `/clients/settings?tab=${tab}` : user.role === 'super-admin' ? `/admins/settings?tab=${tab}`: user.role === 'admin' ? `/admins/settings?tab=${tab}` : `/hellocians/settings?tab=${tab}`, { replace: true });
    setActiveTab(tab);
  };

  const handleTabChange = (tab) => {
    updateUrlWithTab(tab);
  };

  // Debounced Username availability check
  useEffect(() => {
    if (formData.username === user?.username) {
        setUsernameStatus(null);
        return;
    }

    if (formData.username.length < 3) {
        setUsernameStatus('invalid');
        return;
    }

    setUsernameStatus('checking');
    const timer = setTimeout(async () => {
        try {
            const res = await checkUsername(formData.username);
            setUsernameStatus(res.available ? 'available' : 'taken');
        } catch (err) {
            console.error('Check username failed', err);
            setUsernameStatus(null);
        }
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.username, user?.username, checkUsername]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to Cloudinary
    setIsUploading(true);
    const data = new FormData();
    data.append('file', file);

    const uploadPreset = process.env.CLOUDINARY_UPLOAD_PRESET || 'foogles'; 
    const cloudName = process.env.CLOUDINARY_CLOUD_NAME || 'djstk2ukm';
    data.append('upload_preset', uploadPreset);

    try {
      const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: 'POST',
        body: data
      });
      const cloudData = await res.json();
      if (cloudData.secure_url) {
        setProfileImage(cloudData.secure_url);
        setAlert({ type: 'success', message: 'Image uploaded to Cloudinary! Be sure to save.' });
      } else {
        throw new Error('Failed to get URL');
      }
    } catch (err) {
      console.error(err);
      setAlert({ type: 'warning', message: `Cloudinary not configured or failed. Using local preview.` });
      setProfileImage(file);
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const picUrl = typeof profileImage === 'string' ? profileImage : user?.profilePicture;
      
      const payload = { 
        firstName: formData.firstName, 
        lastName: formData.lastName, 
        profilePicture: picUrl,
        address: formData.address,
        city: formData.city,
        postalCode: formData.postalCode,
        country: formData.country,
        timeZone: formData.timeZone 
      };
      const res = await updateProfile(payload);
      
      const stateObj = useAuthStore.getState();
      setTokens({ ...stateObj, user: res.user });
      setAlert({ type: 'success', message: 'Profile updated successfully!' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to update profile' });
    }
  };

  const handlePreferencesSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await updatePreferences({
        directMessages: formData.directMessages,
        timeZone: formData.timeZone,
      });
      const stateObj = useAuthStore.getState();
      setTokens({ ...stateObj, user: res.user });
      setAlert({ type: 'success', message: 'Preferences updated successfully!' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to update preferences' });
    }
  };

  const logoutAndRedirect = () => {
    clearTokens();
    setAlert({ type: 'info', message: 'Security change successful. Please log in again.' });
    navigate(user.role === 'user' ? `/clients/signin` : user.role === 'super-admin' ? `/admins/signin` : user.role === 'admin' ? `/admins/signin` : `/clients/signin`);
  };

  const handleRequestOTP = async () => {
    try {
      await requestOTP();
      setOtpRequested(true);
      setAlert({ type: 'success', message: 'Verification code sent to your email!' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to send OTP' });
    }
  };

  const handleUpdateUsername = async () => {
     try {
       setPendingAction('username');
       await requestOTP();
       setIsOTPModalOpen(true);
       setAlert({ type: 'success', message: 'Verification code sent to your email!' });
     } catch (err) {
       setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to request code' });
     }
  }

  const handleUpdateEmail = async () => {
    try {
      setPendingAction('email');
      await requestOTP();
      setIsOTPModalOpen(true);
      setAlert({ type: 'success', message: 'Verification code sent to your email!' });
    } catch (err) {
       setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to request code' });
    }
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
       setAlert({ type: 'error', message: 'All fields are required' });
       return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setAlert({ type: 'error', message: 'Passwords do not match' });
      return;
    }
    try {
      setPendingAction('password');
      await requestOTP();
      setIsOTPModalOpen(true);
      setAlert({ type: 'success', message: 'Verification code sent to your email!' });
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to request code' });
    }
  }

  const handleVerifySettingsChange = async () => {
    if (formData.otpCode.length !== 6) {
      setOtpError('Please enter the 6-letter code');
      return;
    }
    
    setIsSubmittingOTP(true);
    setOtpError('');
    
    try {
      if (pendingAction === 'username') {
        const res = await updateUsername({ username: formData.username, code: formData.otpCode });
        const stateObj = useAuthStore.getState();
        setTokens({ ...stateObj, user: res.user });
        setAlert({ type: 'success', message: 'Username updated successfully!' });
        closeOTPModal();
      } else if (pendingAction === 'email') {
        await changeEmail({ newEmail: formData.email, code: formData.otpCode });
        closeOTPModal();
        logoutAndRedirect();
      } else if (pendingAction === 'password') {
        await changePassword({ 
          currentPassword: formData.currentPassword, 
          newPassword: formData.newPassword,
          code: formData.otpCode 
        });
        closeOTPModal();
        logoutAndRedirect();
      }
    } catch (err) {
      setOtpError(err.response?.data?.error || 'Verification failed');
    } finally {
      setIsSubmittingOTP(false);
    }
  }

  const closeOTPModal = () => {
    setIsOTPModalOpen(false);
    setPendingAction(null);
    setFormData(prev => ({ ...prev, otpCode: '' }));
    setOtpError('');
  }

  const handleToggle2FA = async () => {
    if (user?.isTwoFactorEnabled) {
      // If already enabled, show disable modal
      setIs2FASetup(false);
      setIs2FAModalOpen(true);
      setTwoFactorCode('');
      setTwoFactorError('');
    } else {
      // If not enabled, generate secret and show setup modal
      try {
        const res = await generate2FA();
        setQrCodeUrl(res.qrCodeUrl);
        setIs2FASetup(true);
        setIs2FAModalOpen(true);
        setTwoFactorCode('');
        setTwoFactorError('');
      } catch (err) {
        setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to initiate 2FA setup' });
      }
    }
  }

  const handleVerify2FASetup = async () => {
    if (twoFactorCode.length !== 6) {
      setTwoFactorError('Please enter the 6-digit code');
      return;
    }
    try {
      await verify2FASetup({ code: twoFactorCode });
      const stateObj = useAuthStore.getState();
      setTokens({ ...stateObj, user: { ...user, isTwoFactorEnabled: true } });
      setAlert({ type: 'success', message: '2FA enabled successfully!' });
      setIs2FAModalOpen(false);
    } catch (err) {
      setTwoFactorError(err.response?.data?.error || 'Verification failed');
    }
  }

  const handleDisable2FA = async () => {
    if (twoFactorCode.length !== 6) {
      setTwoFactorError('Please enter the 6-digit code');
      return;
    }
    try {
      await disable2FA({ code: twoFactorCode });
      const stateObj = useAuthStore.getState();
      setTokens({ ...stateObj, user: { ...user, isTwoFactorEnabled: false } });
      setAlert({ type: 'success', message: '2FA disabled successfully!' });
      setIs2FAModalOpen(false);
    } catch (err) {
      setTwoFactorError(err.response?.data?.error || 'Verification failed');
    }
  }

  const close2FAModal = () => {
    setIs2FAModalOpen(false);
    setQrCodeUrl('');
    setTwoFactorCode('');
    setTwoFactorError('');
  }

  return (
    <div className="bg-gray-100 dark:bg-[#0b0e14] min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div className="text-sm breadcrumbs mb-4 text-gray-500 dark:text-gray-400">
          <span>
          {user.role === 'user' ? 'Clients' : user.role === 'admin' ? 'Admins' : user.role === 'hellocian' ? 'Hellocians' : user.role === 'super-admin' ? 'Super Admins' : 'Clients'}
            </span> / <span>settings</span>
          {activeTab === 'edit-profile' && <span>/edit profile</span>}
          {activeTab === 'account' && <span>/account</span>}
          {activeTab === 'preferences' && <span>/preferences</span>}
          {activeTab === 'security' && <span>/security</span>}
        </div>
        
        <h1 className="text-3xl font-bold text-[#174568] dark:text-white mb-8">Settings</h1>
        
        <div className="bg-white dark:bg-[#191c22] rounded-[25px] border-[1px] border-[#DFEAF2] dark:border-gray-800 overflow-hidden">
          {/* Tabs */}
          <div className="flex md:ml-10 border-b dark:border-gray-800 md:border-none">
            <button
              onClick={() => handleTabChange('edit-profile')}
              className={`px-3 py-2 md:px-6 md:py-4 font-normal md:font-medium text-[10px] md:text-sm transition-all ${
                activeTab === 'edit-profile' ? 'border-b-2 border-[#174568] text-[#174568] dark:text-blue-400 dark:border-blue-400' : 'text-[#9D9D9D] dark:text-gray-500'
              }`}
            >
              Edit Profile
            </button>
            <button
              onClick={() => handleTabChange('account')}
              className={`px-3 py-2 md:px-6 md:py-4 font-normal md:font-medium text-[10px] md:text-sm transition-all ${
                activeTab === 'account' ? 'border-b-2 border-[#174568] text-[#174568] dark:text-blue-400 dark:border-blue-400' : 'text-[#9D9D9D] dark:text-gray-500'
              }`}
            >
              Account
            </button>
            <button
              onClick={() => handleTabChange('preferences')}
              className={`px-3 py-2 md:px-6 md:py-4 font-normal md:font-medium text-[10px] md:text-sm transition-all ${
                activeTab === 'preferences' ? 'border-b-2 border-[#174568] text-[#174568] dark:text-blue-400 dark:border-blue-400' : 'text-[#9D9D9D] dark:text-gray-500'
              }`}
            >
              Preferences
            </button> 
            <button
              onClick={() => handleTabChange('security')}
              className={`px-3 py-2 md:px-6 md:py-4 font-normal md:font-medium text-[10px] md:text-sm transition-all ${
                activeTab === 'security' ? 'border-b-2 border-[#174568] text-[#174568] dark:text-blue-400 dark:border-blue-400' : 'text-[#9D9D9D] dark:text-gray-500'
              }`}
            >
              Security
            </button>
          </div>
          
          {/* Tab Content */}
          <div className="p-6">
            {/* Edit Profile Tab */}
            {activeTab === 'edit-profile' && (
              <form onSubmit={handleProfileSubmit} className='flex flex-col items-center md:flex-row md:items-start gap-5'>
                <div className="mb-6 text-center md:text-left">
                  <div className="relative inline-block">
                    <div className="w-24 h-24 rounded-full bg-blue-100 overflow-hidden">
                      <img 
                        src={previewImage} 
                        alt="Profile" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute bottom-0 right-0 bg-gray-700 rounded-full p-1 cursor-pointer">
                      <label htmlFor="profile-upload" className="cursor-pointer">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </label>
                      <input
                        id="profile-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleImageUpload}
                        disabled={isUploading}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="w-full">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#174568] dark:text-blue-400 mb-2">First Name</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#174568] dark:text-blue-400 mb-2">Last Name</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#174568] dark:text-blue-400 mb-2">Permanent Address</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#174568] dark:text-blue-400 mb-2">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#174568] dark:text-blue-400 mb-2">Postal Code</label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#174568] dark:text-blue-400 mb-2">Country</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
                  <div className="mt-6 text-right">
                  <button
                    type="submit"
                    disabled={isUpdatingProfile}
                    className="w-full md:w-auto bg-[#174568] text-white px-8 py-3 rounded-md hover:bg-gray-800 transition flex items-center justify-center gap-2"
                  >
                    {isUpdatingProfile ? <Loader2 className="animate-spin" size={20} /> : 'Save'}
                  </button>
                </div>
                </div>

               
              </form>
            )}

            {/* Account Tab */}
            {activeTab === 'account' && (
              <div className="max-w-2xl">
                <h3 className="text-[15px] font-medium text-gray-500 mb-6">Account Settings</h3>
                
                <div className="space-y-6">
                  {/* Username Section */}
                  <div className="bg-gray-50 dark:bg-[#1d2026]/50 p-4 rounded-xl border border-gray-200 dark:border-[#32353c]">
                    <label className="block text-sm font-medium text-[#174568] dark:text-blue-400 mb-2">Username</label>
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-col md:flex-row gap-2">
                        <input
                          type="text"
                          name="username"
                          value={formData.username}
                          onChange={handleInputChange}
                          className={`flex-grow p-1.5 md:p-3 border rounded-md dark:bg-[#1d2026] dark:text-white focus:outline-none focus:ring-1 ${
                            usernameStatus === 'available' ? 'border-green-500 focus:ring-green-500' : 
                            usernameStatus === 'taken' || usernameStatus === 'invalid' ? 'border-red-500 focus:ring-red-500' : 
                            'border-gray-300 dark:border-[#32353c] focus:ring-blue-500'
                          }`}
                        />
                        <button 
                          onClick={handleUpdateUsername}
                          disabled={formData.username === user?.username || usernameStatus !== 'available' || isRequestingOTP}
                          className="bg-[#174568] text-white px-2 py-1 md:px-4 md:py-2 rounded-md disabled:opacity-50 text-xs md:text-sm whitespace-nowrap flex items-center justify-center gap-2 min-w-[100px]"
                        >
                          {isRequestingOTP && pendingAction === 'username' ? <Loader2 className="animate-spin" size={20} /> : 'Update'}
                        </button>
                      </div>
                      {usernameStatus === 'checking' && <p className="text-xs text-gray-500">Checking availability...</p>}
                      {usernameStatus === 'available' && <p className="text-xs text-green-600 font-medium flex items-center gap-1"><CheckCircle size={14}/> Username is available</p>}
                      {usernameStatus === 'taken' && <p className="text-xs text-red-600 font-medium">Username already taken</p>}
                      {usernameStatus === 'invalid' && <p className="text-xs text-red-600 font-medium">Username must be at least 3 characters</p>}
                    </div>
                  </div>

                  {/* Email Section */}
                  <div className="bg-gray-50 dark:bg-[#1d2026]/50 p-4 rounded-xl border border-gray-200 dark:border-[#32353c]">
                    <label className="block text-sm font-medium text-[#174568] dark:text-blue-400 mb-2">Email Address</label>
                    <div className="flex flex-col md:flex-row gap-2">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="flex-grow p-1.5 md:p-3 border border-gray-300 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                        <button 
                          onClick={handleUpdateEmail}
                          disabled={formData.email === user?.email || isRequestingOTP}
                          className="bg-[#174568] text-white px-2 py-1 md:px-4 md:py-2 rounded-md disabled:opacity-50 text-xs md:text-sm flex items-center justify-center gap-2 min-w-[100px]"
                        >
                          {isRequestingOTP && pendingAction === 'email' ? <Loader2 className="animate-spin" size={20} /> : 'Update'}
                        </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <form onSubmit={handlePreferencesSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                
                  <div>
                    <label className="block text-sm font-medium text-[#174568] mb-2">Time Zone</label>
                    <select
                      name="timeZone"
                      value={formData.timeZone}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                    >
                      <option value="UTC-10:00">(UTC-10:00) Hawaii</option>
                      <option value="UTC-08:00">(UTC-08:00) Pacific Time</option>
                      <option value="UTC-07:00">(UTC-07:00) Mountain Time</option>
                      <option value="UTC-06:00">(UTC-06:00) Central Time</option>
                      <option value="UTC-05:00">(UTC-05:00) Eastern Time</option>
                      <option value="UTC+00:00">(UTC+00:00) UTC / London</option>
                      <option value="UTC+01:00">(UTC+01:00) Central European Time</option>
                      <option value="UTC+03:00">(UTC+03:00) Moscow</option>
                      <option value="UTC+05:30">(UTC+05:30) India</option>
                      <option value="UTC+08:00">(UTC+08:00) Beijing / Singapore</option>
                      <option value="UTC+09:00">(UTC+09:00) Tokyo</option>
                      <option value="UTC+10:00">(UTC+10:00) Sydney</option>
                    </select>
                  </div>
                </div>
          
                <div className="mb-6 w-[fit-content]">
                  <h3 className="text-sm font-medium text-[#174568] mb-4">Notification</h3>




                  <div className="space-y-4 mb-4">
                    
                   
      
                    <div className="flex items-center gap-4 justify-between">
                      <div className={`w-12 h-6 flex items-center rounded-full p-1 cursor-pointer ${formData.directMessages ? 'bg-[#1FA860]' : 'bg-gray-300'}`}
                        onClick={() => setFormData({...formData, directMessages: !formData.directMessages})}>
                        <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition ${formData.directMessages ? 'translate-x-6' : ''}`}></div>
                      </div>
                      <label htmlFor="direct-messages" className="text-sm text-gray-700 dark:text-gray-300">Allow direct messages</label>
                    </div>
                  

                  </div>




                </div>
                
                <div className="mt-6 text-right">
                  <button
                    type="submit"
                    disabled={isUpdatingPreferences}
                    className="bg-[#174568] text-white px-8 py-3 rounded-[15px] hover:bg-gray-800 transition flex items-center justify-center gap-2 ml-auto"
                  >
                    {isUpdatingPreferences ? <Loader2 className="animate-spin" size={20} /> : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}
            
            {/* Security Tab */}
            {activeTab === 'security' && (
              <form onSubmit={handlePasswordSubmit}>
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-[#B1B1B1] mb-4">Two-factor Authentication</h3>
                  <div className="flex items-center mb-4">
                    <div className={`w-8 h-4 md:w-12 md:h-6 flex items-center rounded-full p-1 cursor-pointer ${user?.isTwoFactorEnabled ? 'bg-[#1FA860] dark:bg-[#1FA860]' : 'bg-gray-300 dark:bg-gray-300'}`}
                      onClick={() => handleToggle2FA()}>
                      <div className={`bg-white w-4 h-4 rounded-full shadow-md transform transition ${user?.isTwoFactorEnabled ? 'translate-x-6' : ''}`}></div>
                    </div>
                    <span className="ml-3 text-[11px] md:text-sm text-[#174568] dark:text-gray-300">
                      {user?.isTwoFactorEnabled ? 'Two-factor authentication is enabled' : 'Two-factor authentication is disabled'}
                    </span>
                  </div>
                </div>
                
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-[#B1B1B1] mb-4">Change Password</h3>
                  <div className="space-y-4">
                    <div className="relative">
                      <label className="block text-sm font-medium text-[#174568] mb-2">Current Password</label>
                      <input
                        type={showCurrentPass ? "text" : "password"}
                        name="currentPassword"
                        value={formData.currentPassword}
                        onChange={handleInputChange}
                        placeholder="••••••••"
                        className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        required
                      />
                      <button 
                        type="button"
                        onClick={() => setShowCurrentPass(!showCurrentPass)}
                        className="absolute right-3 top-[38px] text-gray-400 hover:text-[#174568]"
                      >
                        {showCurrentPass ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-medium text-[#174568] mb-2">New Password</label>
                      <input
                        type={showNewPass ? "text" : "password"}
                        name="newPassword"
                        value={formData.newPassword}
                        onChange={handleInputChange}
                        placeholder="••••••••"
                        className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        required
                      />
                      <button 
                        type="button"
                        onClick={() => setShowNewPass(!showNewPass)}
                        className="absolute right-3 top-[38px] text-gray-400 hover:text-[#174568]"
                      >
                        {showNewPass ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-medium text-[#174568] mb-2">Confirm New Password</label>
                      <input
                        type={showConfirmPass ? "text" : "password"}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder="••••••••"
                        className="w-full p-3 border border-gray-300 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        required
                      />
                      <button 
                        type="button"
                        onClick={() => setShowConfirmPass(!showConfirmPass)}
                        className="absolute right-3 top-[38px] text-gray-400 hover:text-[#174568]"
                      >
                        {showConfirmPass ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 text-right">
                  <button
                    type="submit"
                    disabled={isRequestingOTP}
                    className="w-full md:w-auto bg-[#174568] text-white px-8 py-3 rounded-[15px] hover:bg-gray-800 transition flex items-center justify-center gap-2 ml-auto"
                  >
                    {isRequestingOTP && pendingAction === 'password' ? <Loader2 className="animate-spin" size={20} /> : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      
      {/* OTP Verification Modal */}
      {isOTPModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#0b0e14] rounded-xl w-full max-w-sm overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="px-4 py-1 bg-white dark:bg-[#0b0e14] text-gray-600 dark:text-gray-300 border-b border-gray-200 dark:border-[#32353c] flex items-center justify-between">
              <h4 className="font-medium text-[16px] flex items-center gap-2">
                <Smartphone size={16} />
                Security Verification
              </h4>
              <button 
                onClick={closeOTPModal}
                className="hover:bg-white/20 p-1 rounded-full transition"
              >
                <X size={18} />
              </button>
            </div>
            
            <div className="p-4 text-center">
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-2">
                <CheckCircle className="text-[#174568] w-7 h-7" />
              </div>
              
              <h5 className="text-[14px] font-medium text-gray-800 dark:text-white mb-2">Check Your Email</h5>
              <p className="text-gray-600 dark:text-gray-400 mb-4 text-[12px] font-light">
                We've sent a 6-alphabet verification code to your email address. Please enter it below to authorize this 
                <strong> {pendingAction === 'password' ? 'password change' : `${pendingAction} update`}</strong>.
              </p>
              
              <div className="space-y-2">
                <input
                  type="text"
                  maxLength={6}
                  value={formData.otpCode}
                  onChange={(e) => {
                    setFormData({...formData, otpCode: e.target.value.toUpperCase()});
                    setOtpError('');
                  }}
                  placeholder="ABCDEF"
                  className={`w-full font-normal p-2 border-[2px] rounded-xl text-center tracking-[1em] font-mono text-[15px] focus:outline-none transition-colors ${
                    otpError ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white focus:border-[#174568]'
                  }`}
                />
                
                {otpError && (
                  <p className="text-red-500 text-[12px] font-normal">{otpError}</p>
                )}
                
                <button
                  onClick={handleVerifySettingsChange}
                  disabled={isSubmittingOTP || formData.otpCode.length !== 6}
                  className="w-full text-[13px] font-normal bg-[#174568] text-white py-2 rounded-[10px] font-medium flex items-center justify-center gap-2 hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg active:scale-95"
                >
                  {isSubmittingOTP ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    "Verify & Update"
                  )}
                </button>
                
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-6">
                  Didn't receive code? <button 
                    onClick={requestOTP} 
                    disabled={isRequestingOTP}
                    className="text-[#174568] font-medium hover:underline disabled:opacity-50"
                  >
                    {isRequestingOTP ? 'Sending...' : 'Resend'}
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2FA Setup/Disable Modal */}
      {is2FAModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#1d2026] rounded-xl w-full max-w-sm overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="px-4 py-3 bg-white dark:bg-[#191c22] text-gray-600 dark:text-gray-300 border-b border-gray-200 dark:border-[#32353c] flex items-center justify-between">
              <h4 className="font-medium text-[16px] flex items-center gap-2">
                <Smartphone size={16} />
                {is2FASetup ? 'Setup 2FA' : 'Disable 2FA'}
              </h4>
              <button 
                onClick={close2FAModal}
                className="hover:bg-gray-100 p-1 rounded-full transition"
              >
                <X size={18} />
              </button>
            </div>
            
            <div className="p-6 text-center">
              {is2FASetup ? (
                <>
                  <p className="text-gray-600 dark:text-gray-400 mb-4 text-[13px]">
                    Scan this QR code with your authenticator app (e.g. Google Authenticator, Microsoft Authenticator, Authy).
                  </p>
                  <div className="bg-white p-2 border border-gray-100 rounded-lg inline-block mb-4">
                    <img src={qrCodeUrl} alt="2FA QR Code" className="w-40 h-40" />
                  </div>
                </>
              ) : (
                <p className="text-gray-600 dark:text-gray-400 mb-6 text-[14px]">
                  Please enter the code from your authenticator app to disable 2FA.
                </p>
              )}
              
              <div className="space-y-4">
                <input
                  type="text"
                  maxLength={6}
                  value={twoFactorCode}
                  onChange={(e) => {
                    setTwoFactorCode(e.target.value.replace(/\D/g, ''));
                    setTwoFactorError('');
                  }}
                  placeholder="000000"
                  className={`w-full p-2 border-[2px] rounded-xl text-center tracking-[0.5em] font-mono text-xl focus:outline-none transition-colors ${
                    twoFactorError ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white focus:border-[#174568]'
                  }`}
                />
                
                {twoFactorError && (
                  <p className="text-red-500 text-[12px] font-normal">{twoFactorError}</p>
                )}
                
                <button
                  onClick={is2FASetup ? handleVerify2FASetup : handleDisable2FA}
                  disabled={isVerifying2FA || isDisabling2FA || twoFactorCode.length !== 6}
                  className="w-full text-[13px] font-medium bg-[#174568] text-white py-3 rounded-[10px] flex items-center justify-center gap-2 hover:bg-gray-800 disabled:opacity-50 transition-all shadow-lg active:scale-95"
                >
                  {(isVerifying2FA || isDisabling2FA) ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    is2FASetup ? "Enable 2FA" : "Disable 2FA"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    
    </div>
  );
};

export default SettingsComponent;